import { z } from 'zod'

import { HotmartPostPurchaseStatus } from 'apps/externalTriggers/externalTriggerConstants'
import { IBackendWidget } from 'apps/growthTools/growthToolsInterfaces'
import {
  TriggerEventType,
  TriggerRuleFrequencyType,
  TriggerRuleStatusType,
} from 'apps/triggerRules/models/TriggerRule/constants'
import { ITriggerRuleTrigger } from 'apps/triggerRules/types/triggers'
import { Action } from 'common/actions/models/Action/action'
import { ChannelType } from 'common/core/constants/ChannelType'
import type { IFilter } from 'common/filter/interfaces'
import { TriggerType } from 'common/flow/constants/TriggerType'
import { defaultReplySchema } from 'shared/api/common/schemas/flow/defaultReply'
import { AiIntentSchema } from 'shared/api/requests/aiIntents/schemas'
import { cartReplySchema } from 'shared/api/requests/cartReply/schemas'
import {
  TikTokAdAppType,
  TikTokAdPromotionType,
} from 'shared/api/requests/integrations/tiktokAds/types'
import { KeywordSchema } from 'shared/api/requests/keywords/schemas'
import { storyMentionReplySchema } from 'shared/api/requests/storyMentionReply/schemas'
import { WelcomeMessageSchema } from 'shared/api/requests/welcomeMessage/schemas'

import { FlowContent } from './flowContent'
import { LightFlowSchema } from './lightFlow'

export type FlowContentCoordinates = Record<string, { x: number; y: number }>

const FlowStatsSchema = z.object({
  sent_total: z.number(),
  sent_unq: z.number(),
  delivered_total: z.number(),
  delivered_unq: z.number(),
  read_total: z.number(),
  read_unq: z.number(),
  clicked_total: z.number(),
  clicked_unq: z.number().nullable(),
  earned: z.number().nullable(),
  earned_list: z.array(z.object({})),
  events: z
    .record(z.union([z.string(), z.number()]), z.object({ total: z.number() }))
    .or(z.array(z.unknown())),
})

const NewFlowStatsSchema = z.object({
  sent_unq: z.number(),
  sent: z.number(),
  delivered: z.number(),
  read: z.number(),
  clicked_unq: z.number().nullable(),
  clicked: z.number(),
  sent_total: z.number(),
  delivered_total: z.number(),
  delivered_unq: z.number(),
  read_total: z.number(),
  read_unq: z.number(),
  clicked_total: z.number(),
})

const TriggerRuleSchema = z.object({
  rule_id: z.number(),
  title: z.string(),
  status: z.nativeEnum(TriggerRuleStatusType),
  is_locked: z.boolean(),
  filter: z.custom<IFilter>().nullable(),
  frequency_settings: z.nativeEnum(TriggerRuleFrequencyType).nullable(),
  content_holder: z.string(),
  template_installation_id: z.string().nullable(),
  draft: z
    .object({
      initial_trigger_event_type: z.nativeEnum(TriggerEventType),
    })
    .nullable(),
  ts_created: z.number(),
  ts_updated: z.number(),
  triggers: z.array(z.custom<ITriggerRuleTrigger>()),
  actions: z.array(z.custom<Action>()),
  ctr: z.number().nullish(),
  runs: z.number().nullish(),
  was_activated_once: z.boolean(),
})

const BaseExternalTriggerContextSchema = z.object({
  caption: z.string(),
  description: z.string(),
  name: z.string(),
  type: z.string(),
})

export type ExternalTriggerContext = z.infer<typeof BaseExternalTriggerContextSchema> & {
  fields?: ExternalTriggerContext[]
}

const ExternalTriggerContextSchema: z.ZodType<ExternalTriggerContext> =
  BaseExternalTriggerContextSchema.extend({
    fields: z.lazy(() => z.array(ExternalTriggerContextSchema)).optional(),
  })

const ExternalTriggerAppDataSchema = z.object({
  title: z.string(),
  description: z.string(),
  appTitle: z.string(),
})

const ExternalTriggerHotmartPostPurchaseChangeStatusDataSchema = z.object({
  status: z.nativeEnum(HotmartPostPurchaseStatus),
})

const ExternalTriggerTikTokAdDataSchema = z.object({
  advertiser_id: z.string(),
  campaign_id: z.string(),
  ad_group_id: z.string(),
  ad_id: z.string(),
  ad_name: z.string(),
  promotion_type: z.nativeEnum(TikTokAdPromotionType).nullable(),
  messaging_app_type: z.nativeEnum(TikTokAdAppType).nullable(),
  messaging_app_account_id: z.string().nullable(),
})

const ExternalTriggerDataSchema = z.union([
  ExternalTriggerAppDataSchema,
  ExternalTriggerHotmartPostPurchaseChangeStatusDataSchema,
  ExternalTriggerTikTokAdDataSchema,
])

export const FollowReplyScheme = z.object({
  content_holder: z.string(),
  enabled: z.boolean(),
  flow: LightFlowSchema.nullable().optional(),
})

const ExternalTriggerSchema = z.object({
  app_id: z.number(),
  flow_ns: z.string(),
  external_trigger_id: z.number(),
  title: z.string(),
  data: ExternalTriggerDataSchema.nullable(),
  trigger_key: z.string(),
  trigger_key_name: z.string(),
  context_example: z.string().nullable(),
  status: z.number(),
  status_title: z.string(),
  previous_flow_ns: z.string(),
  ts_created: z.number(),
  ts_updated: z.number(),
  template_installation_id: z.string().nullable(),
  context: z.array(ExternalTriggerContextSchema),
  ctr: z.number().nullish(),
  runs: z.number().nullish(),
  was_activated_once: z.boolean(),
})

const AnotherFlowTriggerSchema = z.object({
  title: z.string(),
  channel: z.nativeEnum(ChannelType).optional(),
  captions: z.array(z.string()).optional(),
  caption: z.string().optional(),
  type: z.enum(['main_menu', 'default']),
  id: z.string(),
  ts_created: z.number().optional(),
})

const ConversationStarterSchema = z.object({
  icebreaker_id: z.number(),
  channel: z.nativeEnum(ChannelType),
  caption: z.string().optional(),
  ctr: z.union([z.number(), z.string()]).nullish(),
  runs: z.number().nullable(),
  clicked_unq: z.number().nullable(),
  type: z.literal(TriggerType.ICE_BREAKER),
})

export const DraftBatchSchema = z.object({
  contents: z.array(z.custom<FlowContent>()).optional(),
  root_content: z.union([z.string(), z.number(), z.null()]).optional(),
})

export const FlowSchema = LightFlowSchema.extend({
  ts_created: z.number(),
  ts_updated: z.number(),
  ts_deleted: z.number().nullable(),
  content_holder: z.string().nullish(),
  flow_stats: NewFlowStatsSchema.nullable(),
  stats: FlowStatsSchema.optional(),
  triggers: z.object({
    widgets: z.array(z.custom<IBackendWidget>()),
    keywords: z.array(KeywordSchema),
    intents: z.array(AiIntentSchema),
    trigger_rules: z.array(TriggerRuleSchema),
    external_triggers: z.array(ExternalTriggerSchema),
    another_flow: z.array(AnotherFlowTriggerSchema),
    default_replies: z.array(defaultReplySchema),
    cart_replies: z.array(cartReplySchema),
    welcome_messages: z.array(WelcomeMessageSchema),
    story_mentions: z.array(storyMentionReplySchema),
    conversation_starters: z.array(ConversationStarterSchema),
    follow_reply: z.array(FollowReplyScheme),
  }),

  external_triggers: z.array(ExternalTriggerSchema),

  // builder
  root_content_id: z.number().nullable(),
  draft_batch: z.array(z.unknown()).or(DraftBatchSchema).nullable(),
  draft_coordinates: z.custom<FlowContentCoordinates>().nullable(),
  contents: z.array(z.custom<FlowContent>()),
  coordinates: z.custom<FlowContentCoordinates>().or(z.array(z.unknown())).nullable(),
})

export type Flow = z.infer<typeof FlowSchema>
export type FlowStats = z.infer<typeof FlowStatsSchema>
export type NewFlowStats = z.infer<typeof NewFlowStatsSchema>
export type TriggerRule = z.infer<typeof TriggerRuleSchema>
export type ExternalTrigger = z.infer<typeof ExternalTriggerSchema>
export type ExternalTriggerData = z.infer<typeof ExternalTriggerDataSchema>
export type ExternalTriggerAppData = z.infer<typeof ExternalTriggerAppDataSchema>
export type ExternalTriggerHotmartPostPurchaseChangeStatusData = z.infer<
  typeof ExternalTriggerHotmartPostPurchaseChangeStatusDataSchema
>
export type ExternalTriggerTikTokAdData = z.infer<typeof ExternalTriggerTikTokAdDataSchema>
export type AnotherFlowTrigger = z.infer<typeof AnotherFlowTriggerSchema>
export type ConversationStarter = z.infer<typeof ConversationStarterSchema>
export type DraftBatch = z.infer<typeof DraftBatchSchema>
export type FollowReply = z.infer<typeof FollowReplyScheme>
